import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { IGetCashToSupplierCalculationBody } from 'services/calculations/cashFlow';

import {
  HALF_NAMES,
  initialPeriodsTableRow,
  IPeriodsTableRow,
  MONTH_NAMES,
  QUARTER_NAMES,
  YEARS_NAMES,
} from 'components/BusinessToolForm/constants/periods';
import { convertToOpposite } from 'components/BusinessToolForm/utils/helpers';

import { IFormContentTypes } from '../../utils/form';

export const createCashToSuppliersParams = (content: IFormContentTypes['cashFlowToSuppliers']) => {
  const [
    operationUnit,
    creditPurchasesDays,
    purchaseOnCreditPercentStr,
    inventoryStr,
    inventorySizeInStockPercentStr,
    periodWiseTotalCostOfSales,
    periodWiseTotalPayrollData,
    costOfSalesFixedTable,
    costOfSalesSalesAndMarketingTable,
    costOfSalesGeneralAndAdministrativeTable,
    initAccountPayable,
    nonOperatingExpense,
  ] = content;
  const daysToPay = parseInt(creditPurchasesDays);
  const purchaseOnCreditPercent = Number(purchaseOnCreditPercentStr);
  const inventory = Number(inventoryStr);
  const inventorySizeInStockPercent = Number(inventorySizeInStockPercentStr);
  const costOfSalesSalesAndMarketingDeprecation = costOfSalesSalesAndMarketingTable[2];
  const costOfSalesGeneralAndAdministrativeDeprecation = costOfSalesGeneralAndAdministrativeTable[2];

  const { column0: amortizationIntangibleExpenseStr, column1: amortizationIntangibleGrowthRateStr } =
    costOfSalesFixedTable[3];

  const amortizationIntangibleExpense = Number(amortizationIntangibleExpenseStr);
  const amortizationIntangibleGrowthRate = Number(amortizationIntangibleGrowthRateStr);

  const depreciationFixed = [
    costOfSalesFixedTable[2],
    costOfSalesSalesAndMarketingDeprecation,
    costOfSalesGeneralAndAdministrativeDeprecation,
  ].map((row) => {
    return {
      depreciationFixedExpense: row['column0'],
      depreciationFixedGrowthRate: row['column1'],
    };
  });

  const salariesForProductionStaff = Number(costOfSalesFixedTable[0]['column0']);
  const benefitsAndPayrollTaxes = Number(costOfSalesFixedTable[1]['column0']);
  const salariesForProductionStaffGrowthRate = Number(costOfSalesFixedTable[0]['column1']);
  const benefitsAndPayrollTaxesGrowthRate = Number(costOfSalesFixedTable[1]['column1']);

  const unit = operationUnit ?? DIMENSIONALITY_VALUES.SINGLE;
  const periodWiseTotalPayroll = convertToOpposite(periodWiseTotalPayrollData);

  const cashToSuppliersParams: IGetCashToSupplierCalculationBody = {
    daysToPay,
    purchaseOnCreditPercent,
    amortizationIntangibleExpense,
    amortizationIntangibleGrowthRate,
    inventorySizeInStockPercent,
    inventory,
    periodWiseTotalCostOfSales,
    periodWiseTotalPayroll,
    initAccountPayable,
    nonOperatingExpense,
    salariesForProductionStaff,
    benefitsAndPayrollTaxes,
    salariesForProductionStaffGrowthRate,
    benefitsAndPayrollTaxesGrowthRate,
    depreciationFixed,
  };

  return { cashToSuppliersParams, unit };
};

export const calculateNetCashNetCashInTheBeginningAndEnd = (netSum: IPeriodsTableRow, initial: number) => {
  const netCashInTheBeginning = structuredClone(initialPeriodsTableRow);
  const netCashInTheEnd = structuredClone(initialPeriodsTableRow);

  const calculateValues = (
    sumValues: Record<string, string | number>,
    startValues: Record<string, string | number>,
    endValues: Record<string, string | number>,
    keys: string[],
    initialValue: number,
  ) => {
    startValues[keys[0]] = String(initialValue);
    endValues[keys[0]] = String(Number(sumValues[keys[0]]) + Number(initialValue));

    for (let i = 1; i < keys.length; i++) {
      startValues[keys[i]] = endValues[keys[i - 1]];
      endValues[keys[i]] = String(Number(sumValues[keys[i]]) + Number(startValues[keys[i]]));
    }
  };

  calculateValues(
    netSum.monthValues,
    netCashInTheBeginning.monthValues,
    netCashInTheEnd.monthValues,
    Object.values(MONTH_NAMES),
    initial,
  );
  calculateValues(
    netSum.quarterValues,
    netCashInTheBeginning.quarterValues,
    netCashInTheEnd.quarterValues,
    Object.values(QUARTER_NAMES),
    initial,
  );
  calculateValues(
    netSum.halfValues,
    netCashInTheBeginning.halfValues,
    netCashInTheEnd.halfValues,
    Object.values(HALF_NAMES),
    initial,
  );
  calculateValues(
    netSum.yearsValues,
    netCashInTheBeginning.yearsValues,
    netCashInTheEnd.yearsValues,
    Object.values(YEARS_NAMES),
    initial,
  );

  return { netCashInTheEnd, netCashInTheBeginning };
};
